import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowUpRight,
    Compass,
    TrendingUp,
    Layers,
    BrainCircuit,
    ShieldCheck,
    Sparkles,
    CheckCircle2,
    Circle,
} from "lucide-react";
import { fetchCaseStudies } from "../lib/cmsApi";
import { hydrateCaseStudy } from "../lib/cmsIcons";

const FALLBACK_CASES = [
    {
        id: "01",
        slug: "founder-dependency",
        icon: Compass,
        accent: "text-cyan-300",
        accentBg: "bg-cyan-300/10 border-cyan-300/25",
        ring: "from-cyan-400/30 to-cyan-400/0",
        title: "From Founder Dependency to Operational Clarity",
        tag: "Operational Clarity",
        meta: "IT Services · 45 employees",
        challenge:
            "A growing IT services company had 45 employees, multiple projects, and increasing revenue — but every critical decision depended on the founder. Sales updates lived in spreadsheets, projects were tracked in separate tools, HR operated independently, and leadership lacked visibility into the business. As the company grew, complexity grew faster.",
        turn:
            "The company implemented ApexBrain to connect sales, operations, projects, HR and communication into a single operating system.",
        before: [
            "Leadership spent hours gathering reports",
            "Teams worked in silos",
            "Project delays were discovered too late",
            "Decisions were reactive",
        ],
        after: [
            "Real-time visibility across departments",
            "Faster decision-making",
            "Improved accountability",
            "Leadership focused on growth instead of coordination",
        ],
        impact:
            "The business moved from founder-driven operations to system-driven execution.",
    },
    {
        id: "02",
        slug: "revenue-leaks",
        icon: TrendingUp,
        accent: "text-emerald-300",
        accentBg: "bg-emerald-300/10 border-emerald-300/25",
        ring: "from-emerald-400/30 to-emerald-400/0",
        title: "Closing Revenue Leaks Hidden Across Teams",
        tag: "Revenue Intelligence",
        meta: "Mid-market IT · pipeline-led",
        challenge:
            "An IT company consistently generated leads but struggled to convert them into predictable revenue. Sales blamed delivery, delivery blamed sales, and leadership lacked the data to identify the actual problem. Revenue targets were missed despite strong pipeline numbers.",
        turn:
            "Using ApexBrain's operational intelligence layer, leadership gained visibility into the entire revenue journey — from lead generation to project delivery.",
        discovery:
            "The issue wasn't lead generation. The biggest revenue leak was occurring between proposal submission and client follow-up.",
        before: [
            "Sales and delivery blamed each other",
            "No single view of the revenue journey",
            "Proposals went stale post-submission",
            "Forecasts were guesswork",
        ],
        after: [
            "Standardized workflows across teams",
            "Automated follow-ups after proposals",
            "Shared accountability with one source of truth",
            "Forecast accuracy improved meaningfully",
        ],
        impact:
            "Revenue stopped leaking through disconnected processes — pipeline conversion, deal velocity and forecasting all improved.",
    },
    {
        id: "03",
        slug: "scale-without-chaos",
        icon: Layers,
        accent: "text-amber-glow",
        accentBg: "bg-amber-300/10 border-amber-300/25",
        ring: "from-amber-400/30 to-amber-400/0",
        title: "Scaling from 20 to 100 Employees Without Operational Chaos",
        tag: "Scaling Operations",
        meta: "IT Services · 5× headcount growth",
        challenge:
            "Growth was creating friction. New employees required extensive hand-holding, processes lived in people's heads, knowledge transfer was inconsistent, and managers spent more time coordinating than leading.",
        turn:
            "ApexBrain became the operational backbone of the company. Processes were documented, workflows became standardized, departments became aligned.",
        before: [
            "Tribal knowledge with no system of record",
            "Onboarding was slow and inconsistent",
            "Managers stuck in coordination overhead",
            "Quality varied team to team",
        ],
        after: [
            "Every employee knew what to do, when, and why",
            "Clear ownership for every workflow",
            "Standardized SOPs across departments",
            "Defined metrics of success",
        ],
        impact:
            "Growth no longer increased complexity. The company scaled its workforce while maintaining operational consistency.",
    },
    {
        id: "04",
        slug: "data-to-decisions",
        icon: BrainCircuit,
        accent: "text-violet-300",
        accentBg: "bg-violet-300/10 border-violet-300/25",
        ring: "from-violet-400/30 to-violet-400/0",
        title: "Turning Data into Decisions",
        tag: "Intelligence Layer",
        meta: "Enterprise leadership team",
        challenge:
            "The leadership team had access to reports, dashboards, spreadsheets and meetings — yet decision-making remained slow. There was information everywhere, but intelligence nowhere.",
        turn:
            "ApexBrain unified business data into a single intelligence layer. Instead of asking ‘What happened?’, leaders began asking ‘What should we do next?’",
        before: [
            "Insights buried across 12+ surfaces",
            "Reactive, after-the-fact analysis",
            "Misaligned plans across teams",
            "Slow leadership cycles",
        ],
        after: [
            "Faster leadership decisions",
            "Better resource allocation",
            "Improved forecasting",
            "Greater organizational alignment",
        ],
        impact:
            "The company transitioned from reporting-driven management to intelligence-driven leadership.",
    },
    {
        id: "05",
        slug: "self-running-business",
        icon: ShieldCheck,
        accent: "text-rose-300",
        accentBg: "bg-rose-300/10 border-rose-300/25",
        ring: "from-rose-400/30 to-rose-400/0",
        title: "Building a Business That Runs Without Constant Supervision",
        tag: "Process Leverage",
        meta: "Founder-led IT firm",
        challenge:
            "Every escalation reached the founder. Every approval required leadership intervention. The company had talented people, but no unified system. Growth created dependency instead of leverage.",
        turn:
            "ApexBrain introduced operational visibility, accountability and standardized workflows across departments.",
        before: [
            "Founder bottleneck on every decision",
            "Leadership pulled into operational firefighting",
            "Approvals blocked progress",
            "Talented teams without leverage",
        ],
        after: [
            "Teams gained clarity",
            "Managers gained ownership",
            "Leadership gained confidence",
            "Organization became proactive instead of reactive",
        ],
        impact:
            "The business evolved from people-dependent operations to process-driven execution.",
    },
];

const FALLBACK_SIGNATURE = {
    id: "✦",
    slug: "signature",
    isSignature: true,
    icon: Sparkles,
    accent: "text-electric",
    accentBg: "bg-electric/10 border-electric/30",
    ring: "from-electric/30 to-electric/0",
    title: "From Chaos to Clarity",
    tag: "Signature Case Study",
    meta: "The ApexBrain transformation",
    before: [
        "Multiple tools",
        "Multiple versions of the truth",
        "Multiple meetings to understand what was happening",
    ],
    after: [
        "One system",
        "One source of truth",
        "One intelligence layer connecting the entire company",
    ],
    outcome:
        "Leadership no longer chased information. Information found leadership.",
};

const FALLBACK_ALL = [...FALLBACK_CASES, FALLBACK_SIGNATURE];

export default function CaseStudiesPage() {
    const [cases, setCases] = useState(FALLBACK_ALL);
    const [activeId, setActiveId] = useState(FALLBACK_CASES[0].id);

    useEffect(() => {
        fetchCaseStudies()
            .then((data) => {
                if (data?.cases?.length) {
                    const hydrated = data.cases.map(hydrateCaseStudy);
                    setCases(hydrated);
                    setActiveId(hydrated[0].id);
                }
            })
            .catch(() => {});
    }, []);

    const active = cases.find((c) => c.id === activeId) || cases[0];
    const isSignature = Boolean(active.isSignature);

    return (
        <div className="pt-28 lg:pt-32" data-testid="case-studies-page">
            {/* Hero */}
            <section className="relative overflow-hidden py-16 lg:py-24">
                <div className="absolute inset-0 grid-bg opacity-[0.25] pointer-events-none" />
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                    <p
                        className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4"
                        data-testid="case-studies-eyebrow"
                    >
                        // case studies · proof in production
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                        <h1
                            className="lg:col-span-7 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="case-studies-title"
                        >
                            Real companies. Real chaos.
                            <br />
                            <span className="gradient-text">
                                Real transformation.
                            </span>
                        </h1>
                        <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                            Five operating stories from teams that moved from
                            fragmented tools and founder dependency to a single
                            intelligence layer — and one signature transformation
                            that ties them all together.
                        </p>
                    </div>
                </div>
            </section>

            {/* Tabbed studies */}
            <section
                className="relative pb-24 lg:pb-32"
                data-testid="case-studies-tabbed"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                        {/* Left rail — case list */}
                        <aside className="lg:col-span-4">
                            <div className="lg:sticky lg:top-28 space-y-2">
                                <p className="font-code text-[10px] uppercase tracking-[0.25em] text-zinc-600 px-3 mb-3">
                                    // select a story
                                </p>
                                {cases.map((c) => {
                                    const isActive = activeId === c.id;
                                    return (
                                        <button
                                            key={c.id}
                                            onClick={() => setActiveId(c.id)}
                                            data-testid={`case-tab-${c.slug}`}
                                            className={`w-full group flex items-start gap-3 text-left rounded-xl border px-4 py-3.5 transition ${
                                                isActive
                                                    ? "bg-white/[0.04] border-white/15"
                                                    : "bg-transparent border-white/[0.06] hover:bg-white/[0.02] hover:border-white/10"
                                            }`}
                                        >
                                            <span
                                                className={`mt-0.5 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md border font-code text-[11px] font-semibold tabular-nums ${
                                                    isActive
                                                        ? `${c.accentBg} ${c.accent}`
                                                        : "bg-white/[0.03] border-white/[0.06] text-zinc-500"
                                                }`}
                                            >
                                                {c.id}
                                            </span>
                                            <span className="flex-1 min-w-0">
                                                <span
                                                    className={`block text-[10px] uppercase tracking-[0.2em] font-code mb-1 ${
                                                        isActive
                                                            ? c.accent
                                                            : "text-zinc-600"
                                                    }`}
                                                >
                                                    {c.tag}
                                                </span>
                                                <span
                                                    className={`block font-outfit text-sm font-semibold leading-snug ${
                                                        isActive
                                                            ? "text-white"
                                                            : "text-zinc-400 group-hover:text-zinc-200"
                                                    }`}
                                                >
                                                    {c.title}
                                                </span>
                                            </span>
                                            {isActive && (
                                                <ArrowUpRight
                                                    className={`shrink-0 w-3.5 h-3.5 mt-1 ${c.accent}`}
                                                />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </aside>

                        {/* Right pane — active case */}
                        <div className="lg:col-span-8 min-w-0">
                            <AnimatePresence mode="wait">
                                <motion.article
                                    key={active.id}
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } }}
                                    exit={{ opacity: 0, y: -6, transition: { duration: 0.18, ease: [0.4, 0, 1, 1] } }}
                                    className="relative rounded-2xl border border-white/[0.08] bg-obsidian-200/70 backdrop-blur-sm overflow-hidden"
                                    data-testid={`case-content-${active.slug}`}
                                >
                                    <div
                                        className={`absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br ${active.ring} blur-3xl pointer-events-none`}
                                    />

                                    <header className="relative p-7 sm:p-10 border-b border-white/[0.06]">
                                        <div className="flex flex-wrap items-center gap-3 mb-5">
                                            <span
                                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 ${active.accentBg}`}
                                            >
                                                <active.icon
                                                    className={`w-3.5 h-3.5 ${active.accent}`}
                                                />
                                                <span
                                                    className={`text-[10px] font-code uppercase tracking-[0.2em] font-semibold ${active.accent}`}
                                                >
                                                    case · {active.id}
                                                </span>
                                            </span>
                                            <span className="font-code text-[11px] text-zinc-500">
                                                {active.meta}
                                            </span>
                                        </div>
                                        <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-semibold text-white leading-[1.1]">
                                            {active.title}
                                        </h2>
                                    </header>

                                    <div className="relative p-7 sm:p-10 space-y-9">
                                        {!isSignature && active.challenge && (
                                            <Block
                                                label="// the challenge"
                                                title="What wasn't working"
                                            >
                                                <p className="text-zinc-300 leading-relaxed">
                                                    {active.challenge}
                                                </p>
                                            </Block>
                                        )}

                                        {!isSignature && active.turn && (
                                            <Block
                                                label="// the turning point"
                                                title="What changed"
                                            >
                                                <p className="text-zinc-300 leading-relaxed">
                                                    {active.turn}
                                                </p>
                                            </Block>
                                        )}

                                        {active.discovery && (
                                            <div className="rounded-xl border border-amber-300/20 bg-amber-300/[0.04] p-5">
                                                <p className="font-code text-[10px] uppercase tracking-[0.2em] text-amber-glow mb-2">
                                                    // the discovery
                                                </p>
                                                <p className="text-zinc-200 leading-relaxed">
                                                    {active.discovery}
                                                </p>
                                            </div>
                                        )}

                                        <Block
                                            label="// the transformation"
                                            title="Before · After"
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
                                                <BeforeList
                                                    items={active.before}
                                                />
                                                <AfterList
                                                    items={active.after}
                                                    accent={active.accent}
                                                />
                                            </div>
                                        </Block>

                                        {(active.impact || active.outcome) && (
                                            <div
                                                className={`relative rounded-xl border ${active.accentBg.replace("bg-", "border-").split(" ")[1] || "border-white/10"} bg-gradient-to-br from-obsidian-100 to-obsidian-300 p-6 overflow-hidden`}
                                            >
                                                <p
                                                    className={`font-code text-[10px] uppercase tracking-[0.2em] mb-2 ${active.accent}`}
                                                >
                                                    {isSignature
                                                        ? "// outcome"
                                                        : "// impact"}
                                                </p>
                                                <p className="font-outfit text-lg sm:text-xl text-white leading-snug">
                                                    {active.impact ||
                                                        active.outcome}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </motion.article>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </section>

        
        </div>
    );
}

const Block = ({ label, title, children }) => (
    <div>
        <p className="font-code text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            {label}
        </p>
        <h3 className="font-outfit text-base font-semibold text-white mb-3">
            {title}
        </h3>
        <div className="text-sm sm:text-base">{children}</div>
    </div>
);

const BeforeList = ({ items }) => (
    <div className="relative rounded-xl border border-white/[0.06] bg-obsidian-300/40 p-5">
        <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-code uppercase tracking-[0.2em] text-red-300 bg-red-500/10 border border-red-500/20 rounded-full px-2 py-0.5">
                Before
            </span>
            <span className="font-code text-[10px] text-zinc-600">
                {"// fragmented"}
            </span>
        </div>
        <ul className="space-y-2">
            {items.map((it) => (
                <li
                    key={it}
                    className="flex items-start gap-2.5 text-sm text-zinc-400"
                >
                    <Circle className="shrink-0 w-3 h-3 mt-1 text-zinc-700" />
                    <span className="line-through decoration-zinc-700/70">
                        {it}
                    </span>
                </li>
            ))}
        </ul>
    </div>
);

const AfterList = ({ items, accent }) => (
    <div className="relative rounded-xl border border-electric/25 bg-gradient-to-br from-obsidian-100 to-obsidian-200 p-5 overflow-hidden">
        <div className="absolute -top-14 -right-14 w-32 h-32 bg-electric/15 blur-3xl rounded-full pointer-events-none" />
        <div className="relative flex items-center gap-2 mb-3">
            <span className="text-[10px] font-code uppercase tracking-[0.2em] text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse-glow" />
                After
            </span>
            <span className="font-code text-[10px] text-zinc-500">
                {"// unified"}
            </span>
        </div>
        <ul className="relative space-y-2">
            {items.map((it) => (
                <li
                    key={it}
                    className="flex items-start gap-2.5 text-sm text-zinc-200"
                >
                    <CheckCircle2
                        className={`shrink-0 w-3.5 h-3.5 mt-0.5 ${accent}`}
                    />
                    <span>{it}</span>
                </li>
            ))}
        </ul>
    </div>
);
